/**
 * useBayConfig Hook - FIXED VERSION with Bay1 Support
 *
 * Provides dynamic bay configuration based on the current location.
 * Returns bay information including active bays, display names, and helper functions.
 */

import { useMemo } from 'react';
import { useLocation } from '../contexts/LocationContext';

// CRITICAL FIX: Default to ALL bays (bay1-bay5) instead of just bay2-4
// This ensures we show data for customers using any bay configuration
const DEFAULT_BAY_CONFIG = {
  numberOfBays: 5,
  activeBays: ['bay1', 'bay2', 'bay3', 'bay4', 'bay5'],
  bayDisplayNames: {
    bay1: 'Bay 1',
    bay2: 'Bay 2',
    bay3: 'Bay 3',
    bay4: 'Bay 4',
    bay5: 'Bay 5'
  }
};

// Bay configuration for a location, straight from the location list. Derived
// synchronously (no state/effect), so the first render already has the site's
// own bays (Admin > Sensor Mappings > Bay Selection) instead of the 5-bay
// default. Pages that compute per-bay numbers on mount used to capture the
// default and show empty Bay 3/4/5 cards.
function bayConfigForLocation(locationData) {
  if (!locationData?.bayConfiguration) return DEFAULT_BAY_CONFIG;
  return {
    numberOfBays: locationData.bayConfiguration.numberOfBays || 5,
    activeBays: locationData.bayConfiguration.activeBays || DEFAULT_BAY_CONFIG.activeBays,
    bayDisplayNames: {
      ...DEFAULT_BAY_CONFIG.bayDisplayNames,
      ...locationData.bayConfiguration.bayDisplayNames
    }
  };
}

export const useBayConfig = () => {
  const { selectedLocationId, availableLocations, loading: locationLoading } = useLocation();

  const locationData = !locationLoading && selectedLocationId && availableLocations?.length
    ? availableLocations.find(loc => loc.id === selectedLocationId)
    : null;
  const bayConfig = useMemo(() => bayConfigForLocation(locationData), [locationData]);
  // Wait for the location list; a selected location that is not (yet) in it also counts as loading.
  const loading = !!locationLoading || (!!selectedLocationId && !!availableLocations?.length && !locationData);
  const error = null;

  // Memoized helper functions and computed values
  const helpers = useMemo(() => ({
    // Get array of active bay IDs
    getActiveBays: () => bayConfig.activeBays,

    // Get display name for a bay
    getBayDisplayName: (bayId) => bayConfig.bayDisplayNames[bayId] || bayId,

    // Check if a bay is active
    isBayActive: (bayId) => bayConfig.activeBays.includes(bayId),

    // Get number of active bays
    getBayCount: () => bayConfig.numberOfBays,

    // Create bay data structure (useful for initializing state)
    createBayObject: (defaultValue = null) => {
      const obj = {};
      bayConfig.activeBays.forEach(bay => {
        obj[bay] = defaultValue;
      });
      return obj;
    },

    // Get CSS grid column template for bay layout
    getGridColumns: () => `repeat(${bayConfig.numberOfBays}, 1fr)`,

    // Get all bay options for dropdowns
    getBayOptions: () => {
      return bayConfig.activeBays.map(bayId => ({
        value: bayId,
        label: bayConfig.bayDisplayNames[bayId] || bayId
      }));
    }
  }), [bayConfig]);

  return {
    ...bayConfig,
    ...helpers,
    loading,
    error,
    // Expose raw config for direct access
    config: bayConfig
  };
};

export default useBayConfig;
